import { useEffect } from 'react';

import {
  META_PIXEL_ID,
  OAUTH_SIGNUP_FLAG_KEY,
  allowedDomain,
  allowedMetaPixelDomains
} from '../../common/auth/constant';
import { GlobalWindow, MetaPixelFbq } from '../../utils/types';
import { getSignupFormHiddenFields } from './common';

export function useHubSpot() {
  useEffect(() => {
    if (!allowedDomain.includes(window.location.host)) return;
    const script = document.createElement('script');
    script.src = 'https://js.hs-scripts.com/22397615.js';
    script.async = true;
    document.getElementsByTagName('head')[0].appendChild(script);
    return () => {
      script.remove();
    };
  }, []);
}

export function useMetaPixel() {
  useEffect(() => {
    if (!allowedMetaPixelDomains.includes(window.location.host)) return;
    const globalWindow = window as unknown as GlobalWindow;

    // Prevent injecting multiple times
    if (globalWindow.fbq) return;
    const fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue.push(args);
      }
    } as MetaPixelFbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    globalWindow.fbq = fbq;
    if (!globalWindow._fbq) globalWindow._fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.getElementsByTagName('head')[0].appendChild(script);
    // Initialize and track initial page view
    fbq('init', META_PIXEL_ID);
    fbq('track', 'PageView');
    return () => {
      script.remove();
      delete globalWindow.fbq;
      delete globalWindow._fbq;
    };
  }, []);
}

/**
 * Creates a HubSpot contact via the tracking API after a successful Google OAuth sign-up.
 */
export function useHubSpotOAuthSignupTracking(user?: { email?: string; created?: number }) {
  useEffect(() => {
    if (!user?.email || !user?.created) return;

    // Only act for accounts created within the last 10 minutes (same threshold as isNewUser)
    const isNewUser = user.created >= Date.now() - 1000 * 60 * 10;
    if (!isNewUser) {
      // User already existed — clear the flag so it doesn't leak into later sessions
      localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);
      return;
    }

    if (localStorage.getItem(OAUTH_SIGNUP_FLAG_KEY) !== 'true') return;

    // Clear immediately to prevent duplicate submissions on re-renders
    localStorage.removeItem(OAUTH_SIGNUP_FLAG_KEY);

    const globalWindow = window as unknown as GlobalWindow;
    const _hsq = (globalWindow._hsq = globalWindow._hsq ?? []);
    // Same hidden inputs as SignUpForm (UTM / ad params + usersignupdate)
    _hsq.push(['identify', { email: user.email, ...getSignupFormHiddenFields() }]);
    _hsq.push(['trackPageView']);
  }, [user?.email, user?.created]);
}
